"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { Container } from "@/components/ui/container";
import {
  agents,
  movieScenes,
  STORY_DURATION_MS,
  storySceneAt,
  storySceneStart,
} from "../../../../packages/global-types-ai-discounts/buddy-demo";
import { BuddyStoryFrame } from "./buddy-story-frame";
import styles from "./marketing.module.css";

function subscribeMotion(callback: () => void) {
  const media = window.matchMedia("(prefers-reduced-motion: reduce)");
  media.addEventListener("change", callback);
  return () => media.removeEventListener("change", callback);
}
function subscribeVisibility(callback: () => void) {
  document.addEventListener("visibilitychange", callback);
  return () => document.removeEventListener("visibilitychange", callback);
}
const motionSnapshot = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const visibilitySnapshot = () => !document.hidden;
const serverMotion = () => true;
const serverVisibility = () => false;

export function BuddyStory() {
  const theater = useRef<HTMLDivElement>(null);
  const elapsedRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [inView, setInView] = useState(false);
  const reducedMotion = useSyncExternalStore(
    subscribeMotion,
    motionSnapshot,
    serverMotion,
  );
  const foreground = useSyncExternalStore(
    subscribeVisibility,
    visibilitySnapshot,
    serverVisibility,
  );
  const ended = elapsed >= STORY_DURATION_MS;
  const active = playing && inView && foreground && !reducedMotion && !ended;
  const phase = storySceneAt(elapsed);
  const scene = movieScenes[phase];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.intersectionRatio >= 0.2),
      { threshold: 0.2 },
    );
    if (theater.current) observer.observe(theater.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const start = performance.now() - elapsedRef.current;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      const next = Math.min(STORY_DURATION_MS, performance.now() - start);
      elapsedRef.current = next;
      setElapsed(next);
    }, 100);
    return () => window.clearInterval(timer);
  }, [active]);

  function selectScene(index: number) {
    setPlaying(false);
    elapsedRef.current = storySceneStart(index);
    setElapsed(elapsedRef.current);
  }

  function replay() {
    elapsedRef.current = 0;
    setElapsed(0);
    setPlaying(true);
  }

  return (
    <section id="buddy-story" className={styles.storySection}>
      <Container>
        <div className={styles.storyLayout}>
          <div>
            <p className={styles.eyebrow}>
              One visit. A world of possibilities.
            </p>
            <h2 className={styles.storyHeading}>
              A little help.
              <br />
              At every step.
            </h2>
            <p className={styles.storyLead}>
              Follow one shopper from their first question to checkout. Then see
              how Insights helps you learn from the conversation.
            </p>
            <div className={styles.storyAgents}>
              {agents.map((agent, index) => (
                <div key={agent.key} data-active={scene.agents.includes(index)}>
                  <span aria-hidden="true">{agent.icon}</span>
                  <strong>{agent.name}</strong>
                </div>
              ))}
            </div>
            <div className={styles.sceneContext} aria-live="off">
              <small>
                Scene {phase + 1} of {movieScenes.length}
              </small>
              <h3>{scene.title}</h3>
              <p>{scene.detail}</p>
            </div>
          </div>
          <div>
            <div ref={theater} className={styles.storyTheater} data-playing={active}>
              <div className={styles.sceneHeader}>
                <span>Still Studio · Buddy AI</span>
                <small>Illustrative demo</small>
              </div>
              <div className={styles.storyFrame} aria-live="off">
                <BuddyStoryFrame
                  phase={phase}
                  checkout={
                    elapsed - storySceneStart(7) >= movieScenes[7].duration / 2
                  }
                />
              </div>
              <div className={styles.demoTabs} aria-hidden="true">
                <span data-active={scene.tab === "home"}>Home</span>
                <span data-active={scene.tab === "ai-rep"}>AI Rep</span>
                <span data-active={scene.tab === "cart"}>Cart</span>
              </div>
            </div>
            <div className={styles.storyControls}>
              <div
                className={styles.sceneTrack}
                role="group"
                aria-label="Explore the Buddy story"
              >
                {movieScenes.map((item, index) => (
                  <button
                    type="button"
                    key={item.title}
                    aria-label={`Scene ${index + 1}: ${item.title}`}
                    aria-pressed={phase === index}
                    onClick={() => selectScene(index)}
                  >
                    <span />
                  </button>
                ))}
              </div>
              {!reducedMotion && (
                <button
                  type="button"
                  className={styles.playButton}
                  onClick={ended ? replay : () => setPlaying(!playing)}
                  aria-label={
                    ended
                      ? "Replay story"
                      : playing
                        ? "Pause story"
                        : "Play story"
                  }
                >
                  {ended ? (
                    <RotateCcw size={18} />
                  ) : playing ? (
                    <Pause size={18} />
                  ) : (
                    <Play size={18} />
                  )}
                </button>
              )}
            </div>
            <p className={styles.demoDisclaimer}>
              Sample conversation, products, and offer.
              {reducedMotion && " Motion is off. Choose a scene to explore."}
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}
